import { useTranslation } from "react-i18next";
import {
    discountPercentage as calcDiscountPercentage,
    formatPrice,
} from "@/lib/formatters";
import { getDisplayAmount } from "@/lib/getDisplayAmount";
import type { PriceTotals } from "@/models/price/price.types";

interface PriceSummaryProps {
    totals: PriceTotals | null;
    discountCode: string;
    totalProductCount: number;
    pricesIncludeVat?: boolean | null;
}

export function PriceSummary({
    totals,
    discountCode,
    totalProductCount,
    pricesIncludeVat,
}: PriceSummaryProps) {
    const { t } = useTranslation();
    const subtotal = getDisplayAmount(pricesIncludeVat, totals) ?? 0;
    if (subtotal <= 0) return null;

    const discountedTotal =
        getDisplayAmount(pricesIncludeVat, {
            netAmount: totals?.discountedNetAmount,
            grossAmount: totals?.discountedGrossAmount,
        }) ?? null;
    const finalTotal = discountedTotal ?? subtotal;
    const discountAmount =
        discountedTotal != null ? subtotal - discountedTotal : 0;
    const discountPct =
        discountedTotal != null
            ? calcDiscountPercentage(subtotal, discountedTotal)
            : 0;
    const hasDiscount = discountedTotal != null && discountAmount > 0;

    return (
        <div className="w-full lg:max-w-1/3 flex self-end flex-col gap-2 rounded-2xl border border-gray-200 bg-white px-5 py-4 text-sm text-gray-600">
            <div className="flex justify-between text-lg">
                <span>{t("confirmStep.price.productCount")}</span>
                <span>
                    {t("confirmStep.price.productCountValue", {
                        count: totalProductCount,
                    })}
                </span>
            </div>
            <div className="flex justify-between text-lg">
                <span>{t("confirmStep.price.subtotal")}</span>
                <span
                    className={
                        hasDiscount ? "line-through text-gray-400" : undefined
                    }
                >
                    {formatPrice(subtotal)}
                </span>
            </div>
            {discountCode && discountedTotal != null && (
                <div className="flex justify-between font-semibold text-danger text-lg">
                    <span>
                        {t("confirmStep.price.discount", {
                            percentage: discountPct,
                            code: discountCode,
                        })}
                    </span>
                    <span>-{formatPrice(discountAmount)}</span>
                </div>
            )}

            <div className="mt-4 flex flex-col items-end">
                <p className="text-[10px] font-semibold uppercase tracking-widest text-gray-400">
                    {t(
                        pricesIncludeVat === false
                            ? "confirmStep.price.totalExclVat"
                            : "confirmStep.price.totalInclVat",
                    )}
                </p>
                <p className="text-4xl font-bold text-gray-900">
                    {formatPrice(finalTotal)}
                </p>
            </div>
        </div>
    );
}
